import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { axiosInstance } from '../../../../helpers'
import { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import { TokenBalance, TokenAdjustmentRequest } from '../types'

export const usePropertyTokens = (propertyId: string) => {
    const queryClient = useQueryClient()
    const balanceQueryKey = ['property', propertyId, 'token-balance']

    // Fetch token balance (now includes tokens_per_unit_allocated)
    const {
        data: balanceData,
        isLoading: isLoadingBalance,
        error: balanceError,
    } = useQuery<AxiosResponse<TokenBalance>, Error>({
        queryKey: balanceQueryKey,
        queryFn: () =>
            axiosInstance.get(
                `/account-manager/properties/${propertyId}/token-balance/`,
            ),
        enabled: !!propertyId,
        staleTime: 0,
        cacheTime: 30 * 60 * 1000,
        refetchOnWindowFocus: true,
    })

    // Update tokens per unit allocation
    const updateTokensPerUnit = useMutation({
        mutationFn: (tokensPerUnit: number) =>
            axiosInstance.patch(
                `/account-manager/properties/${propertyId}/token-balance/`,
                { tokens_per_unit_allocated: tokensPerUnit },
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: balanceQueryKey })
            toast.success('Token allocation updated successfully')
        },
        onError: (error: any) => {
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                'Failed to update token allocation'
            // toast.error(errorMessage)
        },
    })

    // Grant tokens
    const grantTokens = useMutation({
        mutationFn: (request: TokenAdjustmentRequest) =>
            axiosInstance.post(
                `/account-manager/properties/${propertyId}/grant-tokens/`,
                request,
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: balanceQueryKey })
            toast.success('Tokens granted successfully')
        },
        onError: (error: any) => {
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                'Failed to grant tokens'
            toast.error(errorMessage)
        },
    })

    // Subtract tokens
    const subtractTokens = useMutation({
        mutationFn: (request: TokenAdjustmentRequest) =>
            axiosInstance.post(
                `/account-manager/properties/${propertyId}/deduct-tokens/`,
                request,
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: balanceQueryKey })
            toast.success('Tokens subtracted successfully')
        },
        onError: (error: any) => {
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                'Failed to subtract tokens'
            toast.error(errorMessage)
        },
    })

    return {
        // Balance (now includes tokens_per_unit_allocated)
        tokenBalance: balanceData?.data,
        isLoadingBalance,
        balanceError,

        // Mutations
        updateTokensPerUnit,
        grantTokens,
        subtractTokens,

        // Loading state
        isLoading: isLoadingBalance,
    }
}
